import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import type { SxProps, Theme } from "@mui/material";
import { Alert, Box, Button, Stack } from "@mui/material";
import documentEventsApi from "@entrilia/shared/api/clients/documentEventsApi";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import VideoPlayer from "@entrilia/shared/components/previewFile/VideoPlayer";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import type { InvestorCommunicationVideoContent } from "../../api/types/investorCommunicationsTypes";
import { useViewOnlyFileUrl } from "../../hooks/useViewOnlyFileUrl";

interface Props {
  videoContent: InvestorCommunicationVideoContent;
  documentViewSessionId?: string;
}

const sendDownloadEvent = async (documentViewSessionId: string) => {
  try {
    await documentEventsApi.ingestDocumentDownloadEvent({
      viewEventsData: [
        {
          sessionId: documentViewSessionId,
          eventData: { download: true },
        },
      ],
    });
  } catch (error) {
    logError(error, "[NewsVideoPlayer] sendDownloadEvent");
  }
};

const playerSx: SxProps<Theme> = { width: "100%", aspectRatio: "16 / 9", bgcolor: "common.black", borderRadius: 1 };

const placeholderSx: SxProps<Theme> = {
  width: "100%",
  aspectRatio: "16 / 9",
  bgcolor: "common.black",
  borderRadius: 1,
  position: "relative",
  overflow: "hidden",
};

const NewsVideoPlayer = ({ videoContent, documentViewSessionId }: Props) => {
  const { effectiveUrl, decryptFailed, canRetry, isRetrying, retryDecrypt } = useViewOnlyFileUrl(videoContent.videoUrl);

  if (decryptFailed) {
    return (
      <Alert
        severity="error"
        action={
          canRetry ? (
            <Button color="inherit" loading={isRetrying} onClick={retryDecrypt}>
              Retry
            </Button>
          ) : undefined
        }
      >
        The video could not be loaded.
      </Alert>
    );
  }

  if (!effectiveUrl) {
    return (
      <Box sx={placeholderSx}>
        {videoContent.thumbnailUrl && (
          <Box
            component="img"
            src={videoContent.thumbnailUrl}
            alt=""
            sx={{ display: "block", width: "100%", height: "100%", objectFit: "contain" }}
          />
        )}
        <InlineLoader overlay />
      </Box>
    );
  }

  const handleDownloadClick = async () => {
    await downloadFileFromUrl(effectiveUrl);

    if (documentViewSessionId) {
      await sendDownloadEvent(documentViewSessionId);
    }
  };

  return (
    <Stack spacing={2}>
      <VideoPlayer
        src={effectiveUrl}
        poster={videoContent.thumbnailUrl}
        documentViewSessionId={documentViewSessionId}
        sx={playerSx}
      />
      {videoContent.allowDownload && (
        <Button
          color="secondary"
          variant="outlined"
          startIcon={<SaveIcon />}
          aria-label="Download video"
          onClick={handleDownloadClick}
          sx={{ alignSelf: "flex-end" }}
        >
          Download
        </Button>
      )}
    </Stack>
  );
};

export default NewsVideoPlayer;
